import moment, { Moment } from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import Calendar from '../components/Calendar'
import DateInput from '../components/DateInput'
import Portal from '../components/Portal'
import Presets from '../components/Presets'
import { PLACEHOLDERS } from '../constants/constants'
import { useDateSelection, useDropdownPosition } from '../hooks'
import { DatePickerProps, PresetType } from '../types'
import { formatDateForInput } from '../utils/dateFormatters'
import { isDateAvailable } from '../utils/dateValidation'
import {
	DropdownContainer,
	ResetButton,
	StyledDatePicker,
} from './DatePicker.styles'

/**
 * Компонент DatePicker для выбора одной даты
 * Объединяет DateInput, Calendar и опциональные Presets
 * Совместим с API существующего GroupDate компонента
 */
const DatePicker: React.FC<DatePickerProps> = ({
	value,
	onChange,
	restrictionType,
	blockedDatesRange,
	variant = 'primary',
	daysRangeDisabled,
	isTodayFilter = false,
	isYesterdayFilter = false,
	showResetButton = false,
	resetButtonPosition = 'right',
	placeholder,
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const [isUserSelected, setIsUserSelected] = useState(false)
	const [activePreset, setActivePreset] = useState<PresetType | null>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLDivElement>(null)
	const dropdownRef = useRef<HTMLDivElement>(null)

	// Инициализация выбранной даты
	const initialDate = value ? moment(value) : null
	const { selectedDate, selectDate } = useDateSelection(initialDate, onChange)

	// Умное позиционирование дропдауна относительно инпута
	const position = useDropdownPosition(isOpen, inputRef, dropdownRef)

	// Обработка кликов вне компонента для закрытия дропдауна
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false)
			}
		}

		const handleScroll = (event: Event) => {
			// Не закрываем если скролл внутри дропдауна
			if (dropdownRef.current?.contains(event.target as Node)) {
				return
			}
			setIsOpen(false)
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
			window.addEventListener('scroll', handleScroll, true)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			window.removeEventListener('scroll', handleScroll, true)
		}
	}, [isOpen])

	const handleToggleCalendar = () => {
		setIsOpen(!isOpen)
	}

	const handleSelectDate = (date: Moment, preset?: PresetType) => {
		selectDate(date)
		setIsUserSelected(true)
		setActivePreset(preset ?? null)
		setIsOpen(false)
	}

	const handleResetToToday = () => {
		const today = moment()
		selectDate(today)
		setIsUserSelected(false)
		setIsOpen(false)
	}

	// Проверка доступности даты с учетом всех ограничений
	const isDateDisabled = (date: Moment): boolean => {
		return !isDateAvailable(date, {
			restrictionType,
			blockedDatesRange,
			daysRangeDisabled,
		})
	}

	// Формирование массива доступных пресетов
	const availablePresets: PresetType[] = []
	if (isTodayFilter) availablePresets.push('today')
	if (isYesterdayFilter) availablePresets.push('yesterday')

	const showPresets = availablePresets.length > 0

	// Форматирование даты для отображения в инпуте
	const formattedValue = selectedDate
		? formatDateForInput(selectedDate)
		: undefined

	// Показываем кнопку сброса когда пользователь выбрал дату вручную
	const showReset = showResetButton && isUserSelected

	return (
		<StyledDatePicker ref={containerRef}>
			{resetButtonPosition === 'left' && showReset && (
				<ResetButton>
					<TypographySecondary
						sx={{ cursor: 'pointer' }}
						onClick={handleResetToToday}
					>
						Сбросить
					</TypographySecondary>
				</ResetButton>
			)}

			{showPresets && (
				<Presets
					activePreset={activePreset}
					availablePresets={availablePresets}
					onSelectPreset={preset => {
						if (preset === 'today') {
							handleSelectDate(moment(), 'today')
						} else if (preset === 'yesterday') {
							handleSelectDate(moment().subtract(1, 'day'), 'yesterday')
						}
					}}
				/>
			)}

			<DateInput
				ref={inputRef}
				value={formattedValue}
				placeholder={placeholder ?? PLACEHOLDERS.SINGLE}
				isOpen={isOpen}
				variant={variant}
				onClick={handleToggleCalendar}
			/>

			<Portal>
				<DropdownContainer
					ref={dropdownRef}
					$isOpen={isOpen}
					$top={position.top}
					$left={position.left}
				>
					<Calendar
						currentDate={selectedDate || moment()}
						selectedDate={selectedDate}
						mode='single'
						onSelectDate={handleSelectDate}
						isDateDisabled={isDateDisabled}
					/>
				</DropdownContainer>
			</Portal>

			{resetButtonPosition !== 'left' && showReset && (
				<ResetButton>
					<TypographySecondary
						sx={{ cursor: 'pointer' }}
						onClick={handleResetToToday}
					>
						Сбросить
					</TypographySecondary>
				</ResetButton>
			)}
		</StyledDatePicker>
	)
}

export default DatePicker
